import React, { useEffect, useRef } from "react";
import { useDisplayActivations } from "@/hooks";
import { useNetwork } from "@/contexts";

import { LoadingOverlay } from "@/components/generic";
import { ActivationQRCodeView } from "../DisplayActivations";
import { DisplayView } from "../DisplayView";

export interface TVActivationFlowProps {
  /** Polling interval for checking activation status (in milliseconds) */
  pollingInterval?: number;
}

export const TVActivationFlow: React.FC<TVActivationFlowProps> = ({
  pollingInterval = 5000,
}) => {
  const {
    activationData,
    loading,
    isInitialized,
    init,
    activationCode,
    lastError,
  } = useDisplayActivations({
    mode: "single", // Using single mode for TV activation flow
    autoLoad: false, // We'll manually initialize
  });

  // Get network status from context (removed testUrl - not available)
  const { isOnline, isOffline, getConnectionStatusText } = useNetwork();

  const hasInitialized = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wasOnlineRef = useRef(isOnline);

  // Track when network status changes
  useEffect(() => {
    const wasOnline = wasOnlineRef.current;
    const isNowOnline = isOnline;

    // Update ref
    wasOnlineRef.current = isNowOnline;

    // Log network status changes
    if (wasOnline !== isNowOnline) {
      console.log(
        `Network status changed: ${wasOnline ? "online" : "offline"} -> ${
          isNowOnline ? "online" : "offline"
        }`
      );
    }
  }, [isOnline]);

  // Initialize activation on component mount (only once and when online)
  useEffect(() => {
    if (!hasInitialized.current && isOnline) {
      hasInitialized.current = true;
      console.log("Initializing TV activation flow");
      init({
        activationCode: undefined, // Start with empty activation code
      });
    }
  }, [init, isOnline]);

  // Handle polling when we have an activation code but aren't activated yet
  useEffect(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only start polling if we have activation code, aren't activated, and are fully connected
    if (activationCode && !activationData?.displayId && !loading && isOnline) {
      console.log("Starting polling for activation code:", activationCode);

      pollingIntervalRef.current = setInterval(() => {
        // Double-check conditions before each poll
        if (
          activationCode &&
          !activationData?.displayId &&
          !loading &&
          isOnline
        ) {
          console.log("Polling activation status for code:", activationCode);
          init({
            activationCode: activationCode,
          });
        } else {
          // Stop polling if conditions are no longer met
          if (pollingIntervalRef.current) {
            console.log("Stopping polling due to changed conditions");
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }, pollingInterval);
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    activationCode,
    activationData?.displayId,
    loading,
    pollingInterval,
    init,
    isOnline,
  ]); // Fixed: added activationData?.displayId to deps

  // Handle error state - but only show errors during initial setup when online
  if (lastError && isOnline && !activationData?.displayId) {
    return <LoadingOverlay visible loadingText={`Error: ${lastError}`} />;
  }

  // Show initial loading when first initializing (only if online)
  if (loading && !isInitialized && isOnline) {
    return <LoadingOverlay visible loadingText="Loading data..." />;
  }

  // Show QR code view when we have activation code but not activated yet (and online)
  if (
    activationData &&
    activationCode &&
    !activationData.displayId &&
    isOnline
  ) {
    return (
      <ActivationQRCodeView
        activationCode={activationCode}
        // We handle polling in this component now, so disable it in QR view
        onPollActivation={undefined} // No-op since we handle polling here
        pollingInterval={pollingInterval}
        isPolling={!!pollingIntervalRef.current} // Pass actual polling status
      />
    );
  }

  // Show display view when activated (works offline with cached data)
  if (activationData && activationData.displayId) {
    return <DisplayView displayId={activationData.displayId} />;
  }

  // Only show offline message if not activated and offline
  if (isOffline) {
    return (
      <LoadingOverlay
        visible
        loadingText={`TV is offline. Please check your internet connection. Status: ${getConnectionStatusText()}`}
      />
    );
  }

  return <LoadingOverlay visible loadingText="Setting up activation..." />;
};
